/**
 * `vector-grow` (`LN-WGT02`, the shape ruled in the `LN-STL-M2` review):
 * `push_back` filling the spare boxes of a block, and the reallocation that
 * moves every element into a bigger block and frees the old one.
 *
 * <div class="pc-widget" data-widget="vector-grow"
 *      data-steps="[{&quot;op&quot;:&quot;push_back(70)&quot;,&quot;values&quot;:[70],&quot;size&quot;:1,&quot;capacity&quot;:1,&quot;realloc&quot;:true,&quot;moved&quot;:0},
 *                   {&quot;op&quot;:&quot;push_back(85)&quot;,&quot;values&quot;:[70,85],&quot;size&quot;:2,&quot;capacity&quot;:2,&quot;realloc&quot;:true,&quot;moved&quot;:1}]"
 *      data-caption="When the block is full, push_back moves every element to a bigger block.">
 *   <p class="pc-widget-fallback">push_back(70) makes a block of 1; push_back(85) moves 70 into a block of 2.</p>
 * </div>
 *
 * `data-steps` (required): the steps in order, the first frame being step 1;
 * each is `{ op, values, size, capacity, realloc, moved }`: `op` the call
 * shown, `values` the elements after it, `size` equal to `values.length`,
 * `capacity` at least `size`, `realloc` true when the elements moved to a new
 * block (the old one drawn freed beside it), `moved` the elements copied from
 * the old block (0 when `realloc` is false). Before step 1 the vector is
 * empty: size 0, capacity 0, no block. `data-caption` (optional). M17 may add
 * an optional per-step `pointer` without changing this markup. Data that does
 * not hold together (a size that is not the length, a capacity under it, a
 * move without a reallocation) leaves the fallback.
 */

import {
  asText,
  attachStepper,
  buildShell,
  el,
  isIndex,
  isNonEmptyString,
  isRecord,
  isStringOrNumber,
  liveNote,
  parseJson,
  type WidgetText,
} from "./dom";

interface GrowStep {
  op: string;
  values: (string | number)[];
  size: number;
  capacity: number;
  realloc: boolean;
  moved: number;
}

/** More boxes than this is not a picture a reader can count. */
const MAX_CAPACITY = 256;

const isSteps = (value: unknown): value is GrowStep[] =>
  Array.isArray(value) &&
  value.length > 0 &&
  value.every(
    (step) =>
      isRecord(step) &&
      isNonEmptyString(step.op) &&
      Array.isArray(step.values) &&
      step.values.every(isStringOrNumber) &&
      isIndex(step.size) &&
      step.size === step.values.length &&
      isIndex(step.capacity) &&
      step.capacity >= step.size &&
      step.capacity <= MAX_CAPACITY &&
      typeof step.realloc === "boolean" &&
      isIndex(step.moved) &&
      (step.realloc || step.moved === 0),
  );

function drawBlock(
  values: (string | number)[],
  capacity: number,
  copied: number,
  className: string,
  label: string,
): HTMLDivElement {
  const block = el("div", `pc-vec-block ${className}`);
  block.append(el("span", "pc-vec-block-label", label));
  const row = el("div", "pc-widget-cells pc-widget-cells--tight");
  for (let i = 0; i < capacity; i++) {
    const cell = el("div", i < values.length ? "pc-cell" : "pc-cell is-spare");
    cell.append(el("span", "pc-cell-value", i < values.length ? asText(values[i]) : ""));
    cell.append(el("span", "pc-cell-meta", `[${i}]`));
    if (i < copied) cell.classList.add("is-copied");
    row.append(cell);
  }
  block.append(row);
  return block;
}

export function renderVectorGrow(host: HTMLElement, text: WidgetText): boolean {
  const steps = parseJson(host.dataset.steps, isSteps);
  if (!steps) return false;

  const shell = buildShell(host, {
    label: text("labelVector"),
    caption: host.dataset.caption,
  });

  const op = el("p", "pc-vec-op");
  const blocks = el("div", "pc-vec-blocks");
  const note = liveNote();
  shell.body.append(op, blocks, note);

  attachStepper(
    shell,
    steps.length,
    (index) => {
      const step = steps[index];
      const before = index > 0 ? steps[index - 1] : null;

      op.textContent = "";
      op.append(el("code", "pc-vec-call", step.op));
      op.append(el("span", "pc-vec-stats", `size() ${step.size} · capacity() ${step.capacity}`));

      blocks.textContent = "";
      if (step.realloc && before && before.capacity > 0) {
        blocks.append(drawBlock(before.values, before.capacity, 0, "is-freed", text("vecOldBlock")));
      }
      blocks.append(
        drawBlock(
          step.values,
          step.capacity,
          step.realloc ? step.moved : 0,
          step.realloc ? "is-new" : "",
          text(step.realloc ? "vecNewBlock" : "vecBlock"),
        ),
      );

      if (!step.realloc) note.textContent = text("vecSame", { size: step.size, capacity: step.capacity });
      else if (!before || before.capacity === 0) note.textContent = text("vecFirst", { capacity: step.capacity });
      else note.textContent = text("vecRealloc", { capacity: step.capacity, moved: step.moved });
    },
    text,
    { liveReadout: false },
  );
  return true;
}
